import {
  ApiResponse,
  FetchOptions,
  ApiErrorResponse,
  ApiSuccessResponse,
} from "../../types/api";
import { API_CONFIG } from "./config";
import { ApiError, handleApiError } from "./errors";
import { handleUnauthorized } from "./auth";
import { toast } from "sonner";
import { TokenManager } from "./token-manager";
import { SessionManager } from "../session/SessionManager";
import { EntryPoint } from "../../types/api";
import * as Sentry from "@sentry/nextjs";

const createHeaders = (options: FetchOptions = {}) => {
  const token = TokenManager.getToken();
  const clientId = TokenManager.getClientId();
  const sessionData = SessionManager.getSessionData();
  const isFunnel = !!(sessionData && sessionData.funnel.type !== "general");
  const sessionId = isFunnel ? sessionData?.sessionId : null;
  const entryPoint = TokenManager.getEntryPoint() || EntryPoint.HOME;

  return {
    ...API_CONFIG.DEFAULT_HEADERS,
    // ...(token && { Authorization: `Bearer ${token}` }),
    "x-client-id": String(clientId ?? 0),
    "x-entry-point": entryPoint,
    ...(sessionId && { "x-session-id": sessionId }),
    ...options.headers,
  };
};

/** Ensure `/api/proxy/.../` so Next.js `trailingSlash: true` does not 308 POST→GET. */
const toProxyPath = (path: string) => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const queryIndex = normalized.indexOf("?");
  const pathname = queryIndex === -1 ? normalized : normalized.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : normalized.slice(queryIndex);
  const withSlash = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return `${withSlash}${query}`;
};

const handleResponse = async <T extends object>(
  response: Response,
  options: FetchOptions = {},
): Promise<ApiResponse<T>> => {
  let data: any;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  // Handle anonymous token from response headers
  const authHeader = response.headers.get("Authorization");
  // if (authHeader && authHeader.startsWith("Bearer ")) {
  //   const token = authHeader.replace("Bearer ", "");
  //   TokenManager.setToken(token);
  // }

  if (!response.ok) {
    const errorResponse: ApiErrorResponse = data || {
      error: {
        message: "An unknown error occurred",
        error: "unknown_error",
        statusCode: response.status,
      },
      meta: {
        success: false,
        timestamp: new Date().toISOString(),
      },
    };

    if (response.status === 401) {
      TokenManager.clearToken();
      handleUnauthorized();
    } else if (response.status === 404) {
      // Handle 404 errors if needed
    }

    const isIgnoredStatus =
      options.ignoreStatusCodes?.includes(response.status) ?? false;

    if (!isIgnoredStatus) {
      const apiError = new ApiError(
        response.status,
        {
          error: errorResponse.error.error,
          message: errorResponse.error.message,
          statusCode: errorResponse.error.statusCode,
        },
        errorResponse.error.error,
      );
      handleApiError(apiError, options.showErrorToast ?? true);

      // Explicitly capture 500 errors to Sentry (handleApiError already does this, but adding extra context)
      if (
        response.status >= 500 &&
        process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true"
      ) {
        Sentry.captureException(apiError, {
          tags: {
            errorType: "client_api_error",
            statusCode: response.status,
            url: response.url,
          },
          extra: {
            url: response.url,
            status: response.status,
            statusText: response.statusText,
            error: errorResponse.error,
          },
          level: "error",
        });
      }
    }

    return errorResponse;
  }

  return data as ApiSuccessResponse<T>;
};

export const clientApi = {
  async get<T extends object>(
    path: string,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers = createHeaders(options);
    const response = await fetch(`/api/proxy${toProxyPath(path)}`, {
      method: "GET",
      headers,
      cache: options.cache || "no-store",
      signal: options.signal,
      ...(options.revalidate !== undefined && {
        next: { revalidate: options.revalidate },
      }),
      ...(options.tags && { next: { tags: options.tags } }),
    });

    return handleResponse<T>(response, options);
  },

  async post<T extends object>(
    path: string,
    body?: any,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers = createHeaders(options);
    const hasBody = body !== undefined && body !== null;
    // Avoid Content-Type: application/json with an empty body (backend 415)
    if (!hasBody) {
      delete (headers as Record<string, string>)["Content-Type"];
    }
    const response = await fetch(`/api/proxy${toProxyPath(path)}`, {
      method: "POST",
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
      cache: options.cache || "no-store",
    });

    return handleResponse<T>(response, options);
  },

  async put<T extends object>(
    path: string,
    body?: any,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers = createHeaders(options);
    const response = await fetch(`/api/proxy${toProxyPath(path)}`, {
      method: "PUT",
      headers,
      body: body ? JSON.stringify(body) : undefined,
      cache: options.cache || "no-store",
    });

    return handleResponse<T>(response, options);
  },

  async patch<T extends object>(
    path: string,
    body?: any,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers = createHeaders(options);
    const response = await fetch(`/api/proxy${toProxyPath(path)}`, {
      method: "PATCH",
      headers,
      body: body ? JSON.stringify(body) : undefined,
      cache: options.cache || "no-store",
    });

    return handleResponse<T>(response, options);
  },

  async delete<T extends object>(
    path: string,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers = createHeaders(options);
    const response = await fetch(`/api/proxy${toProxyPath(path)}`, {
      method: "DELETE",
      headers,
      cache: options.cache || "no-store",
    });

    const result = await handleResponse<T>(response, options);

    if (!("error" in result)) {
      toast.success("با موفقیت حذف شد");
    }

    return result;
  },
};
